/* ==========================================================================
   mt-motion.js — capa base de motion orientada a conversión (vanilla, sin
   dependencias). Cárgalo con defer:
   <script src="{{ 'mt-motion.js' | asset_url }}" defer></script>

   Incluye: reveals escalonados, títulos palabra a palabra, barra sticky de
   compra, vídeos a la vista, marquesinas, antes/después, cifras que suben,
   efectos de ratón (inclinación 3D, foco de luz, botón magnético), historia
   con escena fija y animaciones Lottie (Jitter) cargadas bajo demanda.

   Cada init recibe un "root" para poder reiniciar solo la sección que el
   editor de temas (o un re-render por AJAX) vuelve a pintar:
   window.mtMotion.init(contenedor)
   ========================================================================== */
(() => {
  const html = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const hasIO = 'IntersectionObserver' in window;
  const STAGGER_CAP = 6;

  // Solo se oculta contenido para animarlo si este JS está vivo, no estamos
  // en el editor de temas, el usuario no ha pedido menos movimiento y las
  // animaciones no están apagadas en los ajustes del tema (mtMotionStatic).
  const animate = hasIO && !designMode && !reduceMotion.matches && !window.mtMotionStatic;
  if (animate) html.classList.add('mt-motion');

  const stripTags = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();
  const once = (el, key) => {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  };

  /* 1. Reveals: los que entran en pantalla a la vez, entran escalonados. */
  const revealObserver = animate
    ? new IntersectionObserver((entries) => {
        let i = 0;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.style.setProperty('--mt-i', Math.min(i++, STAGGER_CAP));
          entry.target.classList.add('mt-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 })
    : null;

  // Títulos .mt-split: cada palabra sube desde una máscara. El texto sigue
  // siendo texto (buscadores y lectores de pantalla lo leen igual).
  function splitWords(el) {
    if (el.dataset.mtSplit) return;
    el.dataset.mtSplit = '1';
    let w = 0;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.append(part);
          return;
        }
        const outer = document.createElement('span');
        const inner = document.createElement('span');
        outer.className = 'mt-word';
        inner.className = 'mt-word__inner';
        inner.style.setProperty('--mt-w', Math.min(w++, 12));
        inner.textContent = part;
        outer.append(inner);
        fragment.append(outer);
      });
      node.replaceWith(fragment);
    });
  }

  // Nareva Home: los títulos de las secciones Motion por debajo de la primera
  // pantalla (todas menos portada y bloque de compra) se revelan palabra a
  // palabra sin tener que marcar cada sección a mano.
  const AUTO_SPLIT = '.mtm-section:not(.mtm-hero):not(.mtm-product) .mtm-head__title';

  function initReveals(root) {
    if (animate) {
      root.querySelectorAll(AUTO_SPLIT).forEach((el) => el.classList.add('mt-split'));
      root.querySelectorAll('.mt-split').forEach(splitWords);
    }
    root.querySelectorAll('.mt-reveal:not(.mt-visible), .mt-split:not(.mt-visible)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('mt-visible');
    });
  }

  /* 2. Barra sticky de compra. No tiene formulario propio: pulsa el botón
     real, así respeta la variante, el carrito del tema y sus apps. */
  function initStickyAtc(root) {
    if (!hasIO) return;
    root.querySelectorAll('[data-mt-sticky-atc]').forEach((bar) => {
      if (!once(bar, 'mtReady')) return;

      const buttonSelector = bar.dataset.mainButton || 'form[action*="/cart/add"] [type="submit"]';
      const page = document.querySelector('main') || document.body;
      const section = bar.closest('.shopify-section');
      // Lo normal es renderizar el snippet dentro de la sección de producto;
      // si no, se busca el formulario en toda la página.
      const scope = section && section.querySelector(buttonSelector) ? section : page;
      const stickyBtn = bar.querySelector('[data-mt-sticky-btn]');
      const stickyPrice = bar.querySelector('[data-mt-sticky-price]');
      const stickyCompare = bar.querySelector('[data-mt-sticky-compare]');
      const variantsEl = bar.querySelector('[data-mt-variants]');
      let variants = {};
      try { variants = JSON.parse(variantsEl ? variantsEl.textContent : '{}'); } catch (e) { variants = {}; }

      // Modo "after" (página de producto): aparece al pasar el botón.
      // Modo "always" (landing): aparece en cuanto se deja atrás la portada y,
      // si el bloque de compra aún está más abajo, al pulsarla baja hasta él.
      const mode = bar.dataset.mtStickyMode === 'always' ? 'always' : 'after';
      let mainBtn = null;
      let pastButton = false;
      let buttonBelow = false;
      let nearFooter = false;
      let shownOnce = false;

      const update = () => {
        const beyondHero = window.scrollY > window.innerHeight * 0.8;
        const show = !nearFooter && (pastButton || (mode === 'always' && buttonBelow && beyondHero));
        bar.classList.toggle('is-shown', show);
        bar.setAttribute('aria-hidden', String(!show));
        html.classList.toggle('mt-sticky-on', show);
        if (show && !shownOnce) {
          shownOnce = true;
          if (stickyBtn && !reduceMotion.matches) stickyBtn.classList.add('mt-shine--play');
        }
      };

      const buttonObserver = new IntersectionObserver(([entry]) => {
        pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        buttonBelow = !entry.isIntersecting && entry.boundingClientRect.top > 0;
        update();
      });
      if (mode === 'always') {
        let ticking = false;
        window.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            update();
          });
        }, { passive: true });
      }

      const footer = document.querySelector(bar.dataset.hideAt || 'footer');
      if (footer) {
        new IntersectionObserver(([entry]) => {
          nearFooter = entry.isIntersecting;
          update();
        }).observe(footer);
      }

      // Shopify puede sustituir el botón al cambiar de variante: se vuelve a buscar.
      const resolveMain = () => {
        const btn = Array.from(scope.querySelectorAll(buttonSelector)).find((b) => !bar.contains(b));
        if (btn && btn !== mainBtn) {
          mainBtn = btn;
          buttonObserver.disconnect();
          buttonObserver.observe(btn);
        }
        return mainBtn;
      };

      const currentVariant = () => {
        const form = mainBtn && mainBtn.form;
        const idField = form && form.querySelector('[name="id"]');
        return idField ? variants[idField.value] : null;
      };

      const isUnavailable = () => {
        const variant = currentVariant();
        return !mainBtn || mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true' || (variant && !variant.available);
      };

      const sync = () => {
        if (!resolveMain()) return;
        const variant = currentVariant();
        if (variant && stickyPrice) {
          const price = stripTags(variant.price);
          if (stickyPrice.textContent !== price) stickyPrice.textContent = price;
        }
        if (variant && stickyCompare) {
          const compare = stripTags(variant.compare);
          if (stickyCompare.textContent !== compare) stickyCompare.textContent = compare;
          stickyCompare.hidden = !compare;
        }
        if (stickyBtn) {
          const unavailable = isUnavailable();
          const label = unavailable ? stripTags(mainBtn.innerText) || stickyBtn.dataset.label : stickyBtn.dataset.label;
          stickyBtn.classList.toggle('is-unavailable', unavailable);
          if (label && stickyBtn.textContent !== label) stickyBtn.textContent = label;
        }
      };

      let queued = false;
      const queueSync = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          sync();
        });
      };
      scope.addEventListener('change', queueSync);
      new MutationObserver(queueSync).observe(scope, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'value'],
      });

      if (stickyBtn) {
        stickyBtn.addEventListener('click', () => {
          resolveMain();
          if (buttonBelow) {
            // Landing: el bloque de compra está más abajo; se baja hasta él para
            // que el cliente vea opciones y packs antes de añadir.
            const block = (section && section.querySelector('#comprar')) || section || mainBtn;
            block.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
            return;
          }
          if (isUnavailable()) {
            // Agotado o combinación no disponible: llevar a elegir otra opción.
            const picker = scope.querySelector('variant-selects, variant-radios, [data-mt-variant-picker]') || (mainBtn && mainBtn.form) || mainBtn;
            if (picker) picker.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
            return;
          }
          mainBtn.click();
        });
      }

      sync();
    });
  }

  /* 3. Vídeos demo: solo se reproducen mientras están a la vista. */
  const videoObserver = hasIO
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) {
            const playing = target.play();
            if (playing) playing.catch(() => {});
          } else {
            target.pause();
          }
        });
      }, { threshold: 0.25 })
    : null;

  function initVideos(root) {
    root.querySelectorAll('video[data-mt-autoplay], [data-mt-autoplay] video').forEach((video) => {
      if (!once(video, 'mtReady')) return;
      video.muted = true;
      video.playsInline = true;
      video.loop = !video.closest('[data-mt-loop="false"]');
      video.removeAttribute('autoplay');
      if (reduceMotion.matches || !videoObserver) {
        video.pause();
        video.controls = true;
        return;
      }
      videoObserver.observe(video);
    });
  }

  /* 4. Marquesinas: en pausa fuera de pantalla (ahorra CPU y batería). */
  const marqueeObserver = hasIO
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => target.classList.toggle('mt-paused', !isIntersecting));
      })
    : null;

  function initMarquees(root) {
    if (!marqueeObserver) return;
    root.querySelectorAll('.mt-marquee').forEach((marquee) => {
      if (once(marquee, 'mtReady')) marqueeObserver.observe(marquee);
    });
  }

  /* 5. Antes/después: arrastre con el dedo o el ratón + teclado (input range). */
  function initBeforeAfter(root) {
    root.querySelectorAll('.mt-ba').forEach((el) => {
      if (!once(el, 'mtReady')) return;
      const range = el.querySelector('.mt-ba__range');
      const set = (pct) => {
        const value = Math.max(0, Math.min(100, pct));
        el.style.setProperty('--mt-pos', value + '%');
        if (range) range.value = String(Math.round(value));
      };
      const fromPointer = (event) => {
        const rect = el.getBoundingClientRect();
        set(((event.clientX - rect.left) / rect.width) * 100);
      };
      let dragging = false;
      el.addEventListener('pointerdown', (event) => {
        dragging = true;
        el.setPointerCapture(event.pointerId);
        fromPointer(event);
      });
      el.addEventListener('pointermove', (event) => {
        if (dragging) fromPointer(event);
      });
      const stop = () => { dragging = false; };
      el.addEventListener('pointerup', stop);
      el.addEventListener('pointercancel', stop);
      if (range) range.addEventListener('input', () => set(Number(range.value)));
    });
  }

  /* 6. Cifras que suben (solo datos reales). El número escrito tal cual
     ("12.000", "4,8") ya está en el HTML; el JS lo cuenta desde 0 una vez. */
  const parseNumber = (text) => {
    const match = String(text).trim().match(/^(\d{1,3}(?:[.,\s]\d{3})+|\d+)(?:[.,](\d{1,2}))?$/);
    if (!match) return null;
    const integer = Number(match[1].replace(/[.,\s]/g, ''));
    const decimals = match[2] ? match[2].length : 0;
    return { value: decimals ? integer + Number(match[2]) / Math.pow(10, decimals) : integer, decimals };
  };
  const countObserver = animate
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          countObserver.unobserve(target);
          const { value, format } = target.__mtCount;
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / 1400);
            target.textContent = format.format(value * (1 - Math.pow(1 - t, 4)));
            if (t < 1) requestAnimationFrame(tick);
            else target.textContent = target.dataset.mtCount;
          };
          requestAnimationFrame(tick);
        });
      }, { threshold: 0.6 })
    : null;

  function initCounters(root) {
    if (!countObserver) return;
    root.querySelectorAll('[data-mt-count]').forEach((el) => {
      const parsed = parseNumber(el.dataset.mtCount);
      if (!parsed || !once(el, 'mtCountReady')) return;
      const format = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        minimumFractionDigits: parsed.decimals,
        maximumFractionDigits: parsed.decimals,
      });
      el.__mtCount = { value: parsed.value, format };
      el.textContent = format.format(0);
      countObserver.observe(el);
    });
  }

  /* 7. Efectos de puntero (solo ratón): inclinación 3D, foco de luz y botón
     magnético. Se calculan una vez por fotograma. */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  function initPointerEffects(root) {
    if (!finePointer.matches || reduceMotion.matches) return;
    root.querySelectorAll('[data-mt-tilt], [data-mt-spotlight], [data-mt-magnetic]').forEach((el) => {
      if (!once(el, 'mtPointer')) return;
      let frame = 0;
      let last = null;
      const apply = () => {
        frame = 0;
        const rect = el.getBoundingClientRect();
        const x = (last.clientX - rect.left) / rect.width;
        const y = (last.clientY - rect.top) / rect.height;
        if ('mtTilt' in el.dataset) {
          el.style.setProperty('--mt-rx', ((0.5 - y) * 8).toFixed(2) + 'deg');
          el.style.setProperty('--mt-ry', ((x - 0.5) * 8).toFixed(2) + 'deg');
        }
        if ('mtSpotlight' in el.dataset) {
          el.style.setProperty('--mt-mx', (x * 100).toFixed(1) + '%');
          el.style.setProperty('--mt-my', (y * 100).toFixed(1) + '%');
        }
        if ('mtMagnetic' in el.dataset) {
          el.style.setProperty('--mt-tx', ((x - 0.5) * 14).toFixed(1) + 'px');
          el.style.setProperty('--mt-ty', ((y - 0.5) * 10).toFixed(1) + 'px');
        }
      };
      el.addEventListener('pointermove', (event) => {
        last = event;
        if (!frame) frame = requestAnimationFrame(apply);
      });
      el.addEventListener('pointerenter', () => el.classList.add('is-pointer'));
      el.addEventListener('pointerleave', () => {
        el.classList.remove('is-pointer');
        ['--mt-rx', '--mt-ry', '--mt-tx', '--mt-ty'].forEach((prop) => el.style.removeProperty(prop));
      });
    });
  }

  /* 8. Historia con escena fija: el paso que cruza el centro de la pantalla
     cambia la imagen fija. Sin JS se ven todos los pasos y la primera imagen. */
  function initStories(root) {
    if (!hasIO) return;
    root.querySelectorAll('[data-mt-story]').forEach((story) => {
      if (!once(story, 'mtStoryReady')) return;
      const steps = Array.from(story.querySelectorAll('[data-mt-story-step]'));
      const media = Array.from(story.querySelectorAll('[data-mt-story-media]'));
      const progress = story.querySelector('[data-mt-story-progress]');
      if (!steps.length) return;
      const activate = (index) => {
        steps.forEach((step, n) => step.classList.toggle('is-active', n === index));
        const shown = Math.min(index, media.length - 1);
        media.forEach((item, n) => item.classList.toggle('is-active', n === shown));
        if (progress) progress.style.setProperty('--mt-progress', String((index + 1) / steps.length));
      };
      story.classList.add('is-enhanced');
      // En móvil la imagen fija ocupa la parte de arriba: el paso se activa en
      // la franja libre de debajo (50-70 % de la pantalla).
      const narrow = window.matchMedia('(max-width: 989px)').matches;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) activate(steps.indexOf(entry.target));
        });
      }, { rootMargin: narrow ? '-50% 0px -30% 0px' : '-45% 0px -45% 0px' });
      steps.forEach((step) => observer.observe(step));
      activate(0);
    });
  }

  /* 9. Animaciones Lottie (las que se exportan de Jitter). El reproductor
     (mt-lottie.min.js, ~46 KB comprimido) solo se descarga si hay alguna
     animación cerca de la pantalla, y cada una solo se reproduce a la vista. */
  let lottieLoading = null;
  const loadLottie = () => {
    if (window.lottie) return Promise.resolve(window.lottie);
    if (!lottieLoading) {
      lottieLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = window.mtLottieSrc;
        script.async = true;
        script.onload = () => resolve(window.lottie);
        script.onerror = reject;
        document.head.append(script);
      });
    }
    return lottieLoading;
  };
  const lottiePlayer = hasIO
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          const anim = target.__mtLottie;
          if (!anim) return;
          if (isIntersecting) anim.play();
          else anim.pause();
        });
      }, { threshold: 0.2 })
    : null;
  const lottieLoader = hasIO
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          lottieLoader.unobserve(target);
          loadLottie()
            .then((lottie) => {
              const anim = lottie.loadAnimation({
                container: target,
                renderer: 'svg',
                loop: target.dataset.mtLottieLoop !== 'false',
                autoplay: false,
                path: target.dataset.mtLottie,
                rendererSettings: { preserveAspectRatio: 'xMidYMid meet', progressiveLoad: true },
              });
              target.__mtLottie = anim;
              anim.addEventListener('DOMLoaded', () => target.classList.add('is-loaded'));
              anim.addEventListener('data_failed', () => target.classList.add('is-error'));
              if (reduceMotion.matches) {
                anim.addEventListener('DOMLoaded', () => anim.goToAndStop(anim.totalFrames - 1, true));
                return;
              }
              lottiePlayer.observe(target);
            })
            .catch(() => target.classList.add('is-error'));
        });
      }, { rootMargin: '300px 0px' })
    : null;

  function initLotties(root) {
    if (!lottieLoader || !window.mtLottieSrc) return;
    root.querySelectorAll('[data-mt-lottie]').forEach((el) => {
      if (once(el, 'mtLottieReady')) lottieLoader.observe(el);
    });
  }

  function init(root) {
    const scope = root || document;
    initReveals(scope);
    initStickyAtc(scope);
    initVideos(scope);
    initMarquees(scope);
    initBeforeAfter(scope);
    initCounters(scope);
    initPointerEffects(scope);
    initStories(scope);
    initLotties(scope);
  }

  window.mtMotion = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Editor de temas: al añadir o editar una sección, se pinta de nuevo.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
